import { AnimateNumber } from '@worldcoo/ui/custom/animate-number';
import type { Locale } from '@worldcoo/monolith/core';
import { useState, useEffect } from 'react';

/**
 * Animated counter that shows donations arriving at their real timestamps.
 *
 * Receives `totalAtWindowStartCents` (server-computed sum of ALL donations
 * before the current window) and an array of donations within the window.
 * Polls Date.now() and adds each donation whose timestamp has been reached,
 * so the counter animates up in real time.
 *
 * Formula: displayedTotal = totalAtWindowStartCents/100 + sum(arrived)/100
 *
 * When `donations` changes (new window), the counter continues smoothly
 * because the new `totalAtWindowStartCents` already includes all previous
 * window donations.
 *
 * Shared rather than owned by `routes/www/`: the redesigned homepage animates the same figure, and
 * leaving the component in the www folder would have taken it away with the rest of that folder at the
 * cutover (TEC-3832). www's two callers keep the defaults, so their output is unchanged.
 */
export function AnimatedTotalRaised({
	totalAtWindowStartCents,
	donations,
	language,
	decimalDigits = 2,
	alwaysGroup = false,
}: {
	totalAtWindowStartCents: number;
	donations: { amountCents: number; timestamp: number }[];
	language: Locale;
	/** www shows the cents; the bento tile is rounded to the euro. */
	decimalDigits?: number;
	/** Force thousands separators — Spanish does not group four digits by default (1303 → "1303"). */
	alwaysGroup?: boolean;
}) {
	// First render must be deterministic (SSR == CSR): `now = 0` means no donation
	// has "arrived" yet, so both sides render `totalAtWindowStartCents / 100`.
	// The real clock only starts after mount, from useEffect.
	const [now, setNow] = useState(0);

	useEffect(() => {
		setNow(Date.now());
		if (donations.length === 0) return;
		const allDone = donations.every((d) => d.timestamp <= Date.now());
		if (allDone) return;
		const timer = setInterval(() => setNow(Date.now()), 50);
		return () => clearInterval(timer);
	}, [donations]);

	const arrivedCents = donations.filter((d) => d.timestamp <= now).reduce((sum, d) => sum + d.amountCents, 0);
	const currentValue = (totalAtWindowStartCents + arrivedCents) / 100;

	return (
		<AnimateNumber
			locale={language}
			value={currentValue}
			suffix="€"
			decimalDigits={decimalDigits}
			alwaysGroup={alwaysGroup}
		/>
	);
}
